import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/authService';
import '../../styles/glass-morphism.css';

function Settings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    try {
      setLoading(true);
      await authService.changePassword(currentPassword, newPassword, confirmPassword);
      setMessage('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div style={styles.container} className="cloud-background internal-page">
      {/* Header */}
      <header style={styles.header} className="glass-container cloudnotes-app-header internal-page-header">
        <div style={styles.headerContent} className="internal-header-content">
          <h1 style={styles.logo}>Settings</h1>
          <div style={styles.headerRight} className="internal-header-actions">
            <span style={styles.username}>Welcome, {user?.username}</span>
            <button className="internal-header-action" onClick={() => navigate('/notes')} style={styles.button}>
              Notes
            </button>
            {user?.role === 'admin' && (
              <button className="internal-header-action" onClick={() => navigate('/admin')} style={styles.button}>
                Admin
              </button>
            )}
            <button className="internal-header-action" onClick={handleLogout} style={styles.button}>
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={styles.main}>
        <div style={styles.content}>
          <div style={styles.card} className="glass-container">
            <h2 style={styles.cardTitle}>Profile Information</h2>
            {searchParams.get('sso_linked') === '1' && (
              <div style={styles.successMessage}>Your Inframodule account is now linked.</div>
            )}
            {searchParams.get('sso_error') && (
              <div style={styles.errorMessage}>The Inframodule account could not be linked. Confirm that both accounts use the same email address.</div>
            )}
            <div style={styles.infoRow}>
              <span style={styles.label}>Username:</span>
              <span style={styles.value}>{user?.username}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.label}>Email:</span>
              <span style={styles.value}>{user?.email}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.label}>Role:</span>
              <span style={styles.value}>{user?.role}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.label}>Inframodule SSO:</span>
              <span style={styles.value}>{user?.oidc_linked ? 'Linked' : 'Not linked'}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.label}>Cloudnotes plan:</span>
              <span style={styles.value}>{user?.plan_tier === 'pro' ? 'Pro' : 'Free'}</span>
            </div>
            {!user?.oidc_linked && (
              <button className="internal-primary-action" type="button" onClick={() => authService.startSso(true)} style={styles.submitButton}>
                Link Inframodule account
              </button>
            )}
            <button className="internal-secondary-action" type="button" onClick={() => navigate('/upgrade')} style={styles.secondaryButton}>
              Plans and billing
            </button>
          </div>

          {!user?.oidc_linked && <div style={styles.card} className="glass-container">
            <h2 style={styles.cardTitle}>Change Password</h2>

            {message && (
              <div style={styles.successMessage}>{message}</div>
            )}

            {error && (
              <div style={styles.errorMessage}>{error}</div>
            )}

            <form onSubmit={handleChangePassword} style={styles.form}>
              <div style={styles.formGroup}>
                <label htmlFor="current-password" style={styles.formLabel}>Current Password</label>
                <input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  style={styles.input}
                  placeholder="Enter current password"
                  disabled={loading}
                />
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="new-password" style={styles.formLabel}>New Password</label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={styles.input}
                  placeholder="Enter new password (min 6 characters)"
                  disabled={loading}
                />
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="confirm-password" style={styles.formLabel}>Confirm New Password</label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={styles.input}
                  placeholder="Confirm new password"
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className="internal-primary-action"
                style={styles.submitButton}
                disabled={loading}
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </button>
            </form>
          </div>}
        </div>
      </main>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    boxSizing: 'border-box',
    background: '#f3f9fc',
    position: 'relative',
  },
  header: {
    flexShrink: 0,
    marginBottom: '20px',
    padding: '16px 24px',
    position: 'relative',
    zIndex: 1,
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    color: 'white',
    fontSize: '1.8rem',
    fontWeight: 'bold',
    margin: 0,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  username: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '1rem',
  },
  button: {
    background: 'rgba(255, 255, 255, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    color: 'white',
    padding: '8px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  main: {
    flex: 1,
    overflow: 'auto',
    position: 'relative',
    zIndex: 1,
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  card: {
    padding: '32px',
  },
  cardTitle: {
    color: '#102a43',
    fontSize: '1.5rem',
    fontWeight: '600',
    marginBottom: '24px',
    marginTop: 0,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid #d9e9f2',
  },
  label: {
    color: '#52677c',
    fontSize: '1rem',
    fontWeight: '500',
  },
  value: {
    color: '#102a43',
    fontSize: '1rem',
    fontWeight: '600',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  formLabel: {
    color: '#334e68',
    fontSize: '0.95rem',
    fontWeight: '500',
  },
  input: {
    padding: '12px',
    border: '1px solid #a9c7d7',
    borderRadius: '8px',
    background: 'white',
    color: '#102a43',
    fontSize: '1rem',
    outline: 'none',
  },
  submitButton: {
    width: '100%',
    marginTop: '16px',
    padding: '14px',
    background: '#0778ad',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  secondaryButton: {
    width: '100%',
    marginTop: '10px',
    padding: '14px',
    background: '#ffffff',
    color: '#04658f',
    border: '1px solid #8bbfd6',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  successMessage: {
    background: '#ecfdf3',
    color: '#067647',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #75e0a7',
  },
  errorMessage: {
    background: '#fef3f2',
    color: '#b42318',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #fda29b',
  },
};

export default Settings;
